/*
 * Acesso às tags sem depender do flarum/tags em tempo de build.
 *
 * O tema funciona sem a extensão de tags: sem ela, `discussion.tags` nem existe
 * no modelo e `app.currentTag` não está definido. O pouco que o tema precisa
 * (nome, cor, ícone, hierarquia) é lido direto do modelo, que é o mesmo nas
 * duas situações.
 */
import app from 'flarum/forum/app';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import classList from 'flarum/common/utils/classList';
import { forumBool, isExtensionEnabled } from './settings';
export function isTagsEnabled() {
  return isExtensionEnabled('flarum-tags');
}
export function allTags() {
  return isTagsEnabled() ? app.store.all('tags') : [];
}

/**
 * Mesma ordem do flarum/tags: as primárias pela posição, filhas logo depois do
 * pai, e as secundárias pelo nome.
 */
export function sortTags(tags) {
  return tags.slice().sort((a, b) => {
    const aParent = a.isChild() ? a.parent() : null;
    const bParent = b.isChild() ? b.parent() : null;
    const aPos = a.position();
    const bPos = b.position();
    if (aParent && aParent === bParent) return (aPos ?? 0) - (bPos ?? 0);
    if (aParent && bParent) return (aParent.position() ?? 0) - (bParent.position() ?? 0);
    if (aParent) return aParent === b ? 1 : (aParent.position() ?? 0) - (bPos ?? 0);
    if (bParent) return bParent === a ? -1 : (aPos ?? 0) - (bParent.position() ?? 0);
    if (aPos === null && bPos === null) return b.discussionCount() - a.discussionCount();
    if (bPos === null) return -1;
    if (aPos === null) return 1;
    return aPos - bPos;
  });
}

/** As "categorias": tags primárias de primeiro nível. */
export function primaryTags() {
  return sortTags(allTags().filter(tag => tag.position() !== null && !tag.isChild()));
}

/** As "etiquetas": tags secundárias, as mais usadas primeiro. */
export function secondaryTags() {
  return allTags().filter(tag => tag.position() === null).sort((a, b) => b.discussionCount() - a.discussionCount());
}
export function discussionTags(discussion) {
  if (!isTagsEnabled()) return [];
  const tags = discussion.tags?.();
  return Array.isArray(tags) ? sortTags(tags.filter(Boolean)) : [];
}

/** Separa as tags da discussão em categoria e etiquetas. */
export function splitDiscussionTags(discussion) {
  const tags = discussionTags(discussion);
  return {
    categories: tags.filter(tag => tag.position() !== null),
    labels: tags.filter(tag => tag.position() === null)
  };
}
export function currentTag() {
  if (!isTagsEnabled()) return null;
  return app.currentTag?.() ?? null;
}

/**
 * Com os endereços próprios das listas ligados, /categories é a página de categorias
 * e /tags, a de etiquetas; sem eles, /tags (do flarum/tags) faz as duas vezes.
 */
export function separateTagsPage() {
  return forumBool('canelaListRoutes', false);
}

/** A rota da página de categorias. */
export function categoriesRouteName() {
  return separateTagsPage() ? 'canela.categories' : 'tags';
}
export function categoriesUrl() {
  return app.route(categoriesRouteName());
}
export function tagUrl(tag) {
  return app.route('tag', {
    tags: tag.slug()
  });
}

/**
 * O símbolo da categoria: o ícone na cor dela ou,
 * sem ícone, um quadradinho preenchido.
 */
export function categorySymbol(tag, className = '') {
  const color = tag?.color() || '';
  const icon = tag?.icon();
  return m("span", {
    className: classList('CanelaCategorySymbol', className, {
      'CanelaCategorySymbol--square': !icon
    }),
    style: color ? {
      '--category-color': color
    } : undefined,
    "aria-hidden": "true"
  }, icon ? m(Icon, {
    name: icon
  }) : null);
}

/**
 * O símbolo da filha sem ícone: o quadradinho em duas cores, a do pai e a
 * dela (`.badge-category.--has-parent::before`).
 */
export function childSymbol(tag, parent) {
  const symbol = categorySymbol(tag);
  if (!tag.icon() && symbol?.attrs) {
    symbol.attrs.className = classList(symbol.attrs.className, 'CanelaCategorySymbol--split');
    symbol.attrs.style = {
      ...(symbol.attrs.style || {}),
      '--category-parent-color': parent.color() || 'var(--cn-primary-medium)'
    };
  }
  return symbol;
}

/**
 * "▪ Suporte" — o badge de categoria, com ou sem link. Com o
 * pai, vira o badge da subcategoria: "▪ Suporte ▪ Instalação", um link só
 * (para a filha).
 */
export function categoryBadge(tag, link = true, parent = null) {
  const content = parent ? [categorySymbol(parent), m("span", {
    className: "CanelaCategoryBadge-name CanelaCategoryBadge-parent"
  }, parent.name()), childSymbol(tag, parent), m("span", {
    className: "CanelaCategoryBadge-name"
  }, tag.name())] : [categorySymbol(tag), m("span", {
    className: "CanelaCategoryBadge-name"
  }, tag.name())];
  // A cor também no badge: na lista, ela pinta o fundo da pílula.
  const attrs = {
    className: classList('CanelaCategoryBadge', {
      'CanelaCategoryBadge--child': !!parent
    }),
    title: tag.description() || undefined,
    style: tag.color() ? {
      '--category-color': tag.color()
    } : undefined
  };
  return link ? m(Link, Object.assign({}, attrs, {
    href: tagUrl(tag)
  }), content) : m("span", attrs, content);
}

/**
 * O ícone da etiqueta como o Flarum o define (na cor dela, quando houver).
 * Sem ícone, `fallback` (ex.: a etiqueta genérica da barra lateral) ou nada.
 * O ícone vai num <span> com `--tag-color` porque a cor é aplicada pelo CSS
 * de cada lugar (lista, barra, busca), que decide o tom quando ela falta.
 */
export function tagSymbol(tag, className = '', fallback = null) {
  const icon = tag?.icon() || fallback;
  if (!icon) return null;
  const color = tag?.color() || '';
  return m("span", {
    className: classList('CanelaTagSymbol', className, {
      'CanelaTagSymbol--colored': !!color
    }),
    style: color ? {
      '--tag-color': color
    } : undefined,
    "aria-hidden": "true"
  }, m(Icon, {
    name: icon
  }));
}

/** Etiqueta (tag secundária) no estilo simples: o ícone do Flarum, se houver, e o texto. */
export function labelBadge(tag, link = true) {
  const attrs = {
    className: 'CanelaTagBadge',
    title: tag.description() || undefined
  };
  const content = [tagSymbol(tag), m("span", null, tag.name())];
  return link ? m(Link, Object.assign({}, attrs, {
    href: tagUrl(tag)
  }), content) : m("span", attrs, content);
}

/** Categoria(s) seguidas das etiquetas, na linha de baixo do título. */
export function discussionBadges(discussion, link = true) {
  const {
    categories,
    labels
  } = splitDiscussionTags(discussion);
  if (!categories.length && !labels.length) return null;

  // Como no Flarum: cada tag no seu badge, a pai uma vez só e cada filha
  // logo depois dela (ordem do `sortTags`), sem repetir o pai na frente.
  return m("span", {
    className: "CanelaBadges"
  }, categories.map(tag => categoryBadge(tag, link)), labels.length > 0 && m("span", {
    className: "CanelaBadges-labels"
  }, labels.map(tag => labelBadge(tag, link))));
}
flarum.reg.add('ramon-canela', 'forum/utils/tags', { isTagsEnabled: isTagsEnabled,allTags: allTags,sortTags: sortTags,primaryTags: primaryTags,secondaryTags: secondaryTags,discussionTags: discussionTags,splitDiscussionTags: splitDiscussionTags,currentTag: currentTag,separateTagsPage: separateTagsPage,categoriesRouteName: categoriesRouteName,categoriesUrl: categoriesUrl,tagUrl: tagUrl,categorySymbol: categorySymbol,childSymbol: childSymbol,categoryBadge: categoryBadge,tagSymbol: tagSymbol,labelBadge: labelBadge,discussionBadges: discussionBadges, });